'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdClose, MdDelete, MdDownload, MdMarkEmailRead, MdSearch, MdUnsubscribe } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import NewsletterAutomationCard from './newsletter-automation-card'
import PaginationControls from '@/components/pagination/PaginationControls'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    exportNewsletterSubscribers,
    getNewsletterSummary,
    listNewsletterStores,
    listNewsletterSubscribers,
    removeNewsletterSubscriber,
    unsubscribeNewsletterSubscriber,
    type StoreNewsletterStatus,
    type StoreNewsletterStore,
    type StoreNewsletterSubscriber,
} from '@/lib/api/commerce/store-newsletter'

type StatusFilter = StoreNewsletterStatus | 'ALL'

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
    { value: 'SUBSCRIBED', label: 'Inscritos' },
    { value: 'UNSUBSCRIBED', label: 'Descadastrados' },
    { value: 'ALL', label: 'Todos' },
]

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Newsletter da loja: quem se inscreveu pelo rodapé, com a prova do consentimento
 * (data, IP e o texto aceito). A planilha exportada leva o link de descadastro de cada
 * inscrito, para a ferramenta de e-mail da loja usar.
 */
export default function StoreNewsletterPage() {
    const { canUpdate, canDelete } = useResourcePermissions('commerce', 'store-newsletter')
    const [searchParams, setSearchParams] = useSearchParams()
    const [stores, setStores] = useState<StoreNewsletterStore[]>([])
    const [storesLoaded, setStoresLoaded] = useState(false)
    const [summary, setSummary] = useState<{ subscribed: number; unsubscribed: number } | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [status, setStatus] = useState<StatusFilter>('SUBSCRIBED')
    const [exporting, setExporting] = useState(false)
    const [pending, setPending] = useState<{ action: 'unsubscribe' | 'remove'; subscriber: StoreNewsletterSubscriber } | null>(null)
    const [working, setWorking] = useState(false)

    const storeParam = Number(searchParams.get('store'))
    const storeId = stores.some((s) => s.id === storeParam) ? storeParam : stores[0]?.id ?? null
    const currentStore = stores.find((s) => s.id === storeId) ?? null
    const debouncedSearch = useDebounce(searchTerm, 300)

    useEffect(() => {
        listNewsletterStores()
            .then((res) => {
                if (res.success) setStores(res.result ?? [])
                else toast.error(res.error || 'Não foi possível carregar as lojas')
            })
            .finally(() => setStoresLoaded(true))
    }, [])

    const loadSummary = useCallback(() => {
        if (!storeId) return
        getNewsletterSummary(storeId).then((res) => {
            if (res.success && res.result) setSummary(res.result)
        })
    }, [storeId])

    useEffect(loadSummary, [loadSummary])

    const filters = useMemo(
        () => ({
            storeId,
            search: debouncedSearch.trim() || undefined,
            status: status === 'ALL' ? undefined : status,
        }),
        [storeId, debouncedSearch, status],
    )

    const fetchFn = useCallback(
        (params: typeof filters & { page: number; limit: number }) =>
            listNewsletterSubscribers(params.storeId!, {
                page: params.page,
                limit: params.limit,
                search: params.search,
                status: params.status,
            }),
        [],
    )

    const { items, loading, page, limit, total, setPage, reload } = usePaginatedPage<StoreNewsletterSubscriber, typeof filters>({
        fetchFn,
        filters,
        limit: 20,
        enabled: !!storeId,
    })

    const selectStore = (id: number) => {
        const next = new URLSearchParams(searchParams)
        next.set('store', String(id))
        setSearchParams(next, { replace: true })
    }

    const handleExport = async () => {
        if (!currentStore) return
        setExporting(true)
        try {
            await exportNewsletterSubscribers(currentStore.id, `newsletter-${currentStore.subdomain}.csv`)
        } catch {
            toast.error('Não foi possível gerar a planilha')
        } finally {
            setExporting(false)
        }
    }

    const confirmPending = async () => {
        if (!pending || !storeId) return
        setWorking(true)
        try {
            const res =
                pending.action === 'remove'
                    ? await removeNewsletterSubscriber(storeId, pending.subscriber.id)
                    : await unsubscribeNewsletterSubscriber(storeId, pending.subscriber.id)
            if (res.success) {
                toast.success(pending.action === 'remove' ? 'Inscrito excluído' : 'Inscrito descadastrado')
                setPending(null)
                reload()
                loadSummary()
            } else {
                toast.error(res.error || 'Não foi possível concluir')
            }
        } finally {
            setWorking(false)
        }
    }

    if (storesLoaded && stores.length === 0) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-3 rounded-xl border border-base-300/50 bg-base-100 p-8 text-center">
                <MdMarkEmailRead className="text-5xl text-base-content/30" />
                <h3 className="text-lg font-semibold">Nenhuma loja online</h3>
                <p className="text-sm text-base-content/60">Crie uma loja em Comércio, Minhas Lojas, para receber inscrições.</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-secondary to-secondary/70 shadow-lg">
                        <MdMarkEmailRead className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="font-bold text-base-content sm:text-lg lg:text-2xl">Newsletter da Loja</h1>
                        <p className="text-sm text-base-content/60">Inscritos pelo rodapé da loja e o envio automático de novidades</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {stores.length > 1 && (
                        <select
                            className="select select-bordered select-sm"
                            value={storeId ?? ''}
                            onChange={(e) => selectStore(Number(e.target.value))}
                            aria-label="Loja"
                        >
                            {stores.map((store) => (
                                <option key={store.id} value={store.id}>
                                    {store.name}
                                </option>
                            ))}
                        </select>
                    )}
                    <button
                        type="button"
                        className="btn btn-secondary btn-sm gap-2"
                        disabled={!currentStore || exporting || !summary?.subscribed}
                        onClick={handleExport}
                        title="Planilha com os inscritos e o link de descadastro de cada um"
                    >
                        <MdDownload className="text-lg" />
                        {exporting ? 'Gerando...' : 'Exportar planilha'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-xl border border-base-300/50 bg-base-100 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">Inscritos</p>
                    <p className="mt-1 text-2xl font-bold text-success">{summary?.subscribed ?? '-'}</p>
                </div>
                <div className="rounded-xl border border-base-300/50 bg-base-100 p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-base-content/60">Descadastrados</p>
                    <p className="mt-1 text-2xl font-bold text-base-content/70">{summary?.unsubscribed ?? '-'}</p>
                </div>
            </div>

            {storeId && <NewsletterAutomationCard key={storeId} storeId={storeId} canUpdate={canUpdate} />}

            <div className="flex flex-col gap-4 rounded-xl border border-base-300/50 bg-base-100 p-4 shadow-sm md:flex-row md:items-center">
                <div className="relative flex-1">
                    <MdSearch className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-xl text-base-content/70" />
                    <input
                        type="text"
                        placeholder="Buscar por e-mail..."
                        className="input input-bordered w-full pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button type="button" onClick={() => setSearchTerm('')} className="btn btn-ghost btn-sm absolute top-1/2 right-2 -translate-y-1/2">
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
                <div role="tablist" className="tabs tabs-box shrink-0">
                    {STATUS_TABS.map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            role="tab"
                            className={`tab ${status === tab.value ? 'tab-active' : ''}`}
                            onClick={() => setStatus(tab.value)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading && items.length === 0 ? (
                <div className="flex min-h-[300px] flex-col items-center justify-center gap-4">
                    <div className="loading loading-spinner loading-lg text-secondary" />
                    <p className="text-sm text-base-content/60">Carregando inscritos...</p>
                </div>
            ) : items.length === 0 ? (
                <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 rounded-xl border border-base-300/50 bg-base-100 p-8 text-center">
                    <MdMarkEmailRead className="text-5xl text-base-content/30" />
                    <h3 className="text-lg font-semibold">{searchTerm ? 'Nenhum inscrito encontrado' : 'Nenhum inscrito ainda'}</h3>
                    <p className="max-w-md text-sm text-base-content/60">
                        {searchTerm
                            ? 'Tente ajustar a busca.'
                            : 'Ligue o bloco "Newsletter" no rodapé da loja, em Temas da Loja, para os visitantes se inscreverem.'}
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {items.map((subscriber) => (
                        <div key={subscriber.id} className="rounded-xl border border-base-300/50 bg-base-100 p-4 shadow-sm">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="break-all font-medium text-base-content">{subscriber.email}</span>
                                        <span className={`badge badge-sm ${subscriber.status === 'SUBSCRIBED' ? 'badge-success' : 'badge-ghost'}`}>
                                            {subscriber.status === 'SUBSCRIBED' ? 'Inscrito' : 'Descadastrado'}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs text-base-content/60">
                                        Consentimento em {formatDateTime(subscriber.consentAt)}
                                        {subscriber.consentIp && `, IP ${subscriber.consentIp}`}
                                        {subscriber.unsubscribedAt && `. Descadastrado em ${formatDateTime(subscriber.unsubscribedAt)}`}
                                    </p>
                                </div>
                                {(canUpdate || canDelete) && (
                                    <div className="flex shrink-0 items-center gap-2">
                                        {canUpdate && subscriber.status === 'SUBSCRIBED' && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm gap-1"
                                                onClick={() => setPending({ action: 'unsubscribe', subscriber })}
                                            >
                                                <MdUnsubscribe className="text-lg" /> Descadastrar
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                                title="Excluir os dados"
                                                onClick={() => setPending({ action: 'remove', subscriber })}
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {total > limit && (
                        <PaginationControls page={page} limit={limit} total={total} loading={loading} onPageChange={setPage} label="inscrito" />
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={!!pending}
                onClose={() => setPending(null)}
                onConfirm={confirmPending}
                title={pending?.action === 'remove' ? 'Excluir inscrito' : 'Descadastrar inscrito'}
                message={
                    pending?.action === 'remove'
                        ? `Os dados de ${pending.subscriber.email} serão apagados de vez, inclusive o registro do consentimento. Use para pedido de eliminação de dados. Esta ação não pode ser desfeita.`
                        : `${pending?.subscriber.email ?? ''} deixa de receber a newsletter e sai da planilha exportada. O registro do consentimento continua guardado.`
                }
                confirmButtonText={pending?.action === 'remove' ? 'Excluir' : 'Descadastrar'}
                cancelButtonText="Cancelar"
                loading={working}
            />
        </div>
    )
}
